import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Loader2, MessageSquare, Reply } from "lucide-react";

import {
  createPermitApplicationComment,
  listPermitApplicationComments,
  type PermitApplicationComment,
} from "@/api/permit_application_comments";
import {
  listPermitApplicationEvents,
  type PermitApplicationEvent,
} from "@/api/permit_application_events";
import { groupComments } from "@/components/comments/groupComments";
import { permitApplicationStatusLabel } from "@/components/permit_suite/permitSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import {
  PERMIT_REVIEWER_DECISION_LABELS,
  type PermitApplicationStatus,
  type PermitReviewerDecision,
} from "@/api/permit_applications";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { invalidatePermitSuitePipeline } from "@/lib/permitSuiteQueryKeys";

type PermitApplicationActivityProps = {
  municipalityId: number;
  applicationId: number;
  fetchEnabled?: boolean;
};

type ActivityEntry =
  | {
      kind: "comment";
      at: string;
      comment: PermitApplicationComment;
      replies: PermitApplicationComment[];
    }
  | {
      kind: "event";
      at: string;
      event: PermitApplicationEvent;
    };

export function buildPermitApplicationActivity(
  events: PermitApplicationEvent[],
  comments: PermitApplicationComment[],
): ActivityEntry[] {
  const { topLevel, repliesByParent } = groupComments(comments);

  const entries: ActivityEntry[] = [
    ...topLevel.map((comment) => ({
      kind: "comment" as const,
      at: comment.created_at,
      comment,
      replies: repliesByParent.get(comment.id) ?? [],
    })),
    ...events.map((event) => ({
      kind: "event" as const,
      at: event.created_at,
      event,
    })),
  ];

  return entries.sort(
    (left, right) => new Date(right.at).getTime() - new Date(left.at).getTime(),
  );
}

function formatStatus(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) return "—";
  const label = permitApplicationStatusLabel(value as PermitApplicationStatus);
  return label ?? value;
}

function formatReviewerDecision(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) return "undecided";
  return PERMIT_REVIEWER_DECISION_LABELS[value as PermitReviewerDecision] ?? value;
}

export function formatEventSummary(event: PermitApplicationEvent): string {
  const metadata = event.metadata ?? {};
  switch (event.event_type) {
    case "status_changed":
      return `Status changed from ${formatStatus(metadata.from)} to ${formatStatus(metadata.to)}`;
    case "deadline_changed":
      return "Deadline updated";
    case "assignee_changed":
      return "Owner updated";
    case "reviewer_added":
      return "Reviewer added";
    case "reviewer_removed":
      return "Reviewer removed";
    case "reviewer_decision_changed":
      return `${metadata.reviewer_name ?? "Reviewer"} decision: ${formatReviewerDecision(metadata.from)} → ${formatReviewerDecision(metadata.to)}`;
    case "task_created":
      return typeof metadata.title === "string" ? `Task added: ${metadata.title}` : "Task added";
    case "task_completed":
      return typeof metadata.title === "string" ? `Task completed: ${metadata.title}` : "Task completed";
    case "task_reopened":
      return typeof metadata.title === "string" ? `Task reopened: ${metadata.title}` : "Task reopened";
    case "task_removed":
      return typeof metadata.title === "string" ? `Task removed: ${metadata.title}` : "Task removed";
    default:
      return String(event.event_type).replace(/_/g, " ");
  }
}

export function PermitApplicationActivity({
  municipalityId,
  applicationId,
  fetchEnabled = true,
}: PermitApplicationActivityProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  const commentsQueryKey = ["permit_application_comments", municipalityId, applicationId] as const;
  const eventsQueryKey = ["permit_application_events", municipalityId, applicationId] as const;

  const { data: comments = [], isLoading: commentsLoading, error: commentsError } = useQuery({
    queryKey: commentsQueryKey,
    queryFn: () => listPermitApplicationComments(municipalityId, applicationId),
    enabled: fetchEnabled,
  });

  const { data: events = [], isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: eventsQueryKey,
    queryFn: () => listPermitApplicationEvents(municipalityId, applicationId),
    enabled: fetchEnabled,
  });

  const activity = useMemo(
    () => buildPermitApplicationActivity(events, comments),
    [events, comments],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    queryClient.invalidateQueries({ queryKey: eventsQueryKey });
    invalidatePermitSuitePipeline(queryClient, municipalityId);
  };

  const createMutation = useMutation({
    mutationFn: ({ body, parentId }: { body: string; parentId?: number | null }) =>
      createPermitApplicationComment(municipalityId, applicationId, body, parentId ?? undefined),
    onSuccess: () => {
      setDraft("");
      setReplyDraft("");
      setReplyingTo(null);
      invalidate();
    },
  });

  const loading = commentsLoading || eventsLoading;
  const feedError = commentsError || eventsError;

  return (
    <div className="space-y-4" data-testid="permit-application-activity">
      <p className="text-sm text-muted-foreground">
        Comments, status changes, reviewer updates, and tasks in one timeline.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading activity…
        </div>
      ) : feedError ? (
        <p className="text-sm text-destructive">Could not load activity.</p>
      ) : activity.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-6 text-center">
          <MessageSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            No activity yet. Post a comment below to start the conversation.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {activity.map((entry) => {
            if (entry.kind === "event") {
              return (
                <li
                  key={`event-${entry.event.id}`}
                  className="rounded-lg border border-dashed bg-background p-3"
                >
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <History className="h-3.5 w-3.5" />
                      <span className="font-medium text-foreground/80">{entry.event.actor_name}</span>
                    </div>
                    <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                  </div>
                  <p className="text-sm">{formatEventSummary(entry.event)}</p>
                </li>
              );
            }

            return (
              <li key={`comment-${entry.comment.id}`} className="rounded-lg border bg-muted/20 p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">{entry.comment.author_name}</span>
                  <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{entry.comment.body}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 px-2 text-xs text-muted-foreground"
                  onClick={() => setReplyingTo(entry.comment.id)}
                >
                  <Reply className="mr-1.5 h-3 w-3" />
                  Reply
                </Button>
                {entry.replies.length > 0 ? (
                  <ul className="mt-3 space-y-2 border-l-2 border-border/60 pl-3">
                    {entry.replies.map((reply) => (
                      <li key={reply.id} className="rounded-md bg-background/80 p-2.5">
                        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/80">{reply.author_name}</span>
                          <time dateTime={reply.created_at}>
                            {formatRelativeTime(reply.created_at)}
                          </time>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{reply.body}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {replyingTo === entry.comment.id ? (
                  <div className="mt-3 space-y-2 rounded-lg border bg-background p-2">
                    <Textarea
                      value={replyDraft}
                      onChange={(event) => setReplyDraft(event.target.value)}
                      rows={2}
                      placeholder="Write a reply…"
                      aria-label="Reply to comment"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={!replyDraft.trim() || createMutation.isPending}
                        onClick={() =>
                          createMutation.mutate({
                            body: replyDraft.trim(),
                            parentId: entry.comment.id,
                          })
                        }
                      >
                        {createMutation.isPending ? "Posting…" : "Post reply"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-2 rounded-lg border bg-background p-3">
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={3}
          placeholder="Leave a note for your team…"
          aria-label="New activity comment"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!draft.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate({ body: draft.trim() })}
          >
            {createMutation.isPending ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
